import { atom, useAtom } from 'jotai'
import { atomFamily } from 'jotai/utils'
import { useEffect } from 'react'
import { type ShopSession } from '@/services/shopSession/ShopSession.types'
import { useShopSession } from '@/services/shopSession/ShopSessionContext'
import { usePriceIntent } from './usePriceIntent'

type PriceCalculatorStep = 'loadingForm' | 'fillForm' | 'calculatingPrice' | 'viewOffers'

// Only used to access in priceCalculatorStepAtom
const currentPriceIntentIdAtom = atom<string | null>(null)
const currentShopSessionAtom = atom<ShopSession | null>(null)

// Maps a priceIntentId to a step
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const priceCalculatorStepAtomFamily = atomFamily((_: string) => atom<PriceCalculatorStep>('loadingForm'))

export const priceCalculatorStepAtom = atom(
  (get) => {
    const priceIntentId = get(currentPriceIntentIdAtom)
    const shopSession = get(currentShopSessionAtom)

    if (priceIntentId == null || shopSession == null) {
      return 'loadingForm'
    }

    return get(priceCalculatorStepAtomFamily(priceIntentId))
  },
  (get, set, value: PriceCalculatorStep) => {
    const priceIntentId = get(currentPriceIntentIdAtom)

    if (priceIntentId == null) {
      throw new Error('priceIntentId must be defined')
    }

    // Get the step atom for the current priceIntentId
    const priceIntentStepAtom = priceCalculatorStepAtomFamily(priceIntentId)

    set(priceIntentStepAtom, value)
  },
)

export const usePriceCalculatorStep = () => {
  const { priceIntentId } = usePriceIntent()
  const { shopSession } = useShopSession()
  const [step, setStep] = useAtom(priceCalculatorStepAtom)
  const [, setCurrentPriceIntentId] = useAtom(currentPriceIntentIdAtom)
  const [, setCurrentShopSession] = useAtom(currentShopSessionAtom)
  useEffect(() => {
    if (priceIntentId) {
      setCurrentPriceIntentId(priceIntentId)
    }

    if (shopSession) {
      setCurrentShopSession(shopSession)
    }
  }, [priceIntentId, shopSession, setCurrentPriceIntentId, setCurrentShopSession])

  return { step, setStep }
}
